'use client'

import React from 'react'
import { SuperPromptsIcon } from '@/components/icons/super-prompts'
import { pushModal } from '@/components/modals'
import {
  MAX_CONTENT_ASSETS,
  UploadAssetsModal,
} from '@/components/modals/upload-assets'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useWorkspaces } from '@/hooks/subscriptions/use-workspaces'
// Removed: useOrchestrateQnaStartup - now using OpenAI Agent SDK intake flow
import { useSuperPromptsStore } from '@/hooks/use-super-prompts-store'
import { useUploadAssets } from '@/hooks/use-upload-assets'
import { useUserCoin } from '@/hooks/use-user-coin'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import type { Asset, User } from '@/lib/types'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import {
  AlertTriangle,
  Box,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Code,
  Copy,
  ImagePlus,
  Layout,
  Loader2,
  Monitor,
  Palette,
  Paperclip,
  ShoppingBag,
  Sparkles,
  Upload,
  Wand2,
  X,
} from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import type { Locale } from '@/i18n/config'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { WorkspaceDropdown } from './workspace-dropdown'
import { GenerateButton } from './generate-button'
import {
  initialPayload,
  TabType,
  getFileIcon,
  TABS,
  getTemplateText,
  TAB_TO_PROJECT_TYPE,
  getTabBuildOption,
} from './_const'
import { PDF_FILE_THUMBNAIL_URL } from '@/lib/constant'
// Removed useMedia import - using optimized custom hook instead
import { GradientTooltip } from '@/components/common/gradient-tooltip'
import { GooglePlacesAutocomplete } from '@/components/google-places-autocomplete'

interface HeroPromptProps {
  user?: User
  isTemplateMode: boolean
  promptPlaceholder?: string
  baseTemplateId?: string | null
  templateData?: any
  initialAssets?: Asset[]
  onAssetsChange?: (assets: Asset[]) => void
}

/**
 * Local business info sent to the API and stored in DB
 * Uses camelCase (placeId) to match API conventions
 */
interface ILocalBusinessInfo {
  name: string
  address: string
  placeId: string | null
  style: string
}

/**
 * Internal data structure for local business mode in hero-prompt
 * Uses snake_case (place_id) to match Google Places API response
 */
interface ILocalBusinessData {
  mode: 'local_business'
  businessName: string
  businessAddress: string
  place_id: string | null
  style: string
  assembledPrompt: string
}

// Placeholder examples are now fetched from i18n translations

const STYLES = [
  {
    id: 'notion',
    title: 'Like Notion',
    bgImage: '/homepage/Light-Style.png',
    styleText: 'Use a light minimal design style (like Notion)',
  },
  {
    id: 'linear',
    title: 'Like Linear',
    bgImage: '/homepage/Dark-Style.png',
    styleText: 'Use a dark minimal design style (like Linear)',
  },
  {
    id: 'nike',
    title: 'Like Nike',
    bgImage: '/homepage/Artistic-Style.png',
    styleText: 'Use an artistic design style (like Nike)',
  },
  {
    id: 'mckinsey',
    title: 'Like McKinsey',
    bgImage: '/homepage/Pro-Style.png',
    styleText: 'Use a professional design style (like McKinsey)',
  },
  {
    id: 'duolingo',
    title: 'Like Duolingo',
    bgImage: '/homepage/GenZ-Style.png',
    styleText: 'Use a Gen Z design style (like Duolingo)',
  },
  {
    id: 'spotify',
    title: 'Like Spotify',
    bgImage: '/homepage/Bold-Style.png',
    styleText: 'Use a bold design style (like Spotify)',
  },
]

/**
 * Strip all leading protocol prefixes from a URL.
 * Handles malformed URLs like "https://https://example.com" or "http://https://example.com"
 * Returns the domain/path without any protocol prefix.
 */
function stripUrlProtocols(url: string): string {
  if (!url) return url
  // Repeatedly remove leading http:// or https:// until none remain
  let cleaned = url.trim()
  while (/^https?:\/\//i.test(cleaned)) {
    cleaned = cleaned.replace(/^https?:\/\//i, '')
  }
  return cleaned
}

const TAB_ICONS: Record<string, any> = {
  website: Monitor,
  store: ShoppingBag,
  landingpage: Layout,
  'ai-app': Sparkles,
  'internal-tool': Code,
  prototype: Box,
}

// Enhanced hero prompt component with glassmorphism design
export function HeroPrompt({
  user,
  initialAssets,
  onAssetsChange,
}: HeroPromptProps) {
  const locale = useLocale()
  const herot = useTranslations('home_v3.hero')
  const modalt = useTranslations('modals')
  const t = useTranslations('ai-chat')
  const [inputValue, setInputValue] = useState('')
  const [payload, setPayload] = useState({
    ...initialPayload,
    contentAssets: initialAssets || [],
  })
  const [showUploadDropdown, setShowUploadDropdown] = useState(false)
  const [showStyleGrid, setShowStyleGrid] = useState(false)
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isTryItNowDisabled, setIsTryItNowDisabled] = useState(false)
  const [tryItNowCountdown, setTryItNowCountdown] = useState(0)
  const [isHighlighted, setIsHighlighted] = useState(false)
  const [isComposing, setIsComposing] = useState(false)
  const [hasEverTyped, setHasEverTyped] = useState(false)
  const [designSuggestionsCollapsed, setDesignSuggestionsCollapsed] =
    useState(false)
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    null,
  )
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedTab, setSelectedTab] = useState<TabType>('website')
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const [placeholderFade, setPlaceholderFade] = useState(true)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [isDraggingTabs, setIsDraggingTabs] = useState(false)
  const [selectedMode, setSelectedMode] = useState<
    'clone' | 'redesign' | 'local_business' | null
  >(null)
  const [cloneUrl, setCloneUrl] = useState('')
  const [redesignOldUrl, setRedesignOldUrl] = useState('')
  // Local business state
  const [localBusinessName, setLocalBusinessName] = useState('')
  const [localBusinessAddress, setLocalBusinessAddress] = useState('')
  const [localBusinessPlaceId, setLocalBusinessPlaceId] = useState<
    string | null
  >(null)
  const [showLocalBusinessStyleGrid, setShowLocalBusinessStyleGrid] =
    useState(false)
  const touchStartX = useRef<number>(0)
  const scrollStartLeft = useRef<number>(0)

  const [removeSuperPromptDialog, setRemoveSuperPromptDialog] = useState<{
    open: boolean
    promptName: string
    promptText: string
    onConfirm: () => void
  }>({
    open: false,
    promptName: '',
    promptText: '',
    onConfirm: () => {},
  })
  const prevInputLengthRef = useRef(0)
  const isInternalUpdateRef = useRef(false)
  const tabsScrollRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const styleUploadInputRef = useRef<HTMLInputElement>(null)

  // Optimized mobile detection using matchMedia instead of window resize
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    // Use matchMedia for efficient media query detection (doesn't listen to resize)
    const mql = window.matchMedia('(max-width: 640px)')
    setIsMobile(mql.matches)

    // Only listen to media query changes, not window resize
    const handleChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches)
    }

    // Modern browsers
    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange)
      return () => mql.removeEventListener('change', handleChange)
    }
    // Fallback for older browsers
    mql.addListener(handleChange)
    return () => mql.removeListener(handleChange)
  }, [])

  // SuperPrompts store for cached data
  const { superPrompts, fetchSuperPrompts } = useSuperPromptsStore()

  // Removed QnA startup hook - now using OpenAI Agent SDK intake flow

  // Workspace subscription hook
  const { workspaces } = useWorkspaces()

  // Project generation requires credits for every user.
  const getGenerateButtonText = () => {
    return herot('generate_button.short')
  }

  // Read URL search params to auto-select clone mode for specific ref codes
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return

    const urlParams = new URLSearchParams(window.location.search)
    const ref = urlParams.get('ref')
    // Auto-select clone website mode when ref=RO2S6VTJ or ref=ES7U6C4R
    if (ref === 'RO2S6VTJ' || ref === 'ES7U6C4R') {
      setSelectedMode('clone')
    }
  }, [])

  // Load saved input value and assets from localStorage on component mount
  useEffect(() => {
    const storedValue = localStorage.getItem('homepage_input_value')
    if (storedValue) {
      setInputValue(storedValue)
    }

    // Check if user has ever typed before
    const hasTyped = localStorage.getItem('homepage_has_typed')
    if (hasTyped === 'true') {
      setHasEverTyped(true)
    }

    const storedPayload = localStorage.getItem('homepage_payload')
    if (storedPayload) {
      try {
        const parsedPayload = JSON.parse(storedPayload)
        setPayload(parsedPayload)
      } catch (error) {
        // console.error('Failed to parse stored payload:', error)
        localStorage.removeItem('homepage_payload')
      }
    }

    // Listen for external changes to localStorage
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'homepage_input_value' && e.newValue !== null) {
        setInputValue(e.newValue)
      }
    }
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  // Fetch super prompts immediately on mount for "Try Free" button
  useEffect(() => {
    if (!superPrompts || superPrompts.length === 0) {
      fetchSuperPrompts()
    }
  }, []) // Empty dependency array - run only on mount

  // Initialize workspace selection from localStorage or default
  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      const storedWorkspaceId = localStorage.getItem('selected_workspace_id')
      // Check if stored workspace still exists in user's workspaces
      const workspaceExists = workspaces.some((sub: any) => {
        return sub._id.toString() === storedWorkspaceId
      })

      if (storedWorkspaceId && workspaceExists) {
        setSelectedWorkspaceId(storedWorkspaceId)
      } else {
        // Default to first workspace (usually the active one)
        const firstWorkspace = workspaces[0] as any
        setSelectedWorkspaceId(firstWorkspace._id.toString())
        localStorage.setItem(
          'selected_workspace_id',
          firstWorkspace._id.toString(),
        )
      }
    }
  }, [workspaces])

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [errorMessage])

  // Rotating placeholder animation
  useEffect(() => {
    const placeholderExamples = herot.raw('placeholder_examples') as string[]
    const placeholderCount = Array.isArray(placeholderExamples)
      ? placeholderExamples.length
      : 5
    const interval = setInterval(() => {
      setPlaceholderFade(false)
      setTimeout(() => {
        setPlaceholderIndex((prev) => (prev + 1) % placeholderCount)
        setPlaceholderFade(true)
      }, 500)
    }, 3000)
    return () => clearInterval(interval)
  }, [herot, locale])

  // Sync content assets changes back to parent (only for internal changes)
  useEffect(() => {
    if (onAssetsChange && isInternalUpdateRef.current) {
      onAssetsChange(payload.contentAssets)
      isInternalUpdateRef.current = false // Reset flag
    }
  }, [payload.contentAssets, onAssetsChange])

  // Update payload when initialAssets change from parent
  useEffect(() => {
    if (initialAssets && !isInternalUpdateRef.current) {
      setPayload((prev) => ({
        ...prev,
        contentAssets: initialAssets,
      }))
    }
  }, [initialAssets])

  // Handle Try It Now countdown timer
  useEffect(() => {
    if (tryItNowCountdown > 0) {
      const timer = setTimeout(() => {
        setTryItNowCountdown(tryItNowCountdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (isTryItNowDisabled && tryItNowCountdown === 0) {
      setIsTryItNowDisabled(false)
    }
  }, [tryItNowCountdown, isTryItNowDisabled])

  // Handle highlight effect
  useEffect(() => {
    if (isHighlighted) {
      const timer = setTimeout(() => {
        setIsHighlighted(false)
      }, 1000) // Remove highlight after 1 second
      return () => clearTimeout(timer)
    }
  }, [isHighlighted])

  // Save payload to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('homepage_payload', JSON.stringify(payload))
  }, [payload])

  // Show DesignSuggestions on first type for desktop only, then manage collapse state
  useEffect(() => {
    // Skip auto-show logic on mobile
    if (isMobile) {
      return
    }

    const currentLength = inputValue.trim().length
    const prevLength = prevInputLengthRef.current

    // On first type ever (going from 0 to something for the first time)
    if (!hasEverTyped && prevLength === 0 && currentLength > 0) {
      setHasEverTyped(true)
      localStorage.setItem('homepage_has_typed', 'true')
      setDesignSuggestionsCollapsed(false)
    }
    // After component is visible, auto-expand when going from empty to something
    else if (hasEverTyped && prevLength === 0 && currentLength > 0) {
      setDesignSuggestionsCollapsed(false)
    }
    // Hide when prompt becomes empty
    else if (hasEverTyped && currentLength === 0) {
      setHasEverTyped(false)
      localStorage.removeItem('homepage_has_typed')
    }

    // Update ref for next comparison
    prevInputLengthRef.current = currentLength
  }, [inputValue, hasEverTyped, isMobile])

  // Global function to trigger highlight from TemplateCategories
  useEffect(() => {
    const triggerHighlight = () => {
      // Preserve current input value before highlighting
      const textarea = document.querySelector('textarea') as HTMLTextAreaElement
      if (textarea) {
        const currentValue = textarea.value

        // Update React state first to prevent placeholder flash
        if (currentValue && currentValue !== inputValue) {
          setInputValue(currentValue)
          localStorage.setItem('homepage_input_value', currentValue)
        }

        // Set the highlight state after ensuring state is synchronized
        setTimeout(() => {
          setIsHighlighted(true)

          // Double-check the value stays after the state change
          setTimeout(() => {
            if (textarea.value !== currentValue) {
              textarea.value = currentValue
              setInputValue(currentValue)
              localStorage.setItem('homepage_input_value', currentValue)
            }
          }, 10)
        }, 5) // Very short delay to ensure React state update is processed
      } else {
        setIsHighlighted(true)
      }
    }
    ;(window as any).triggerHeroPromptHighlight = triggerHighlight

    return () => {
      ;(window as any).triggerHeroPromptHighlight = undefined
    }
  }, [inputValue])

  const onChangeInput = useCallback((e: any) => {
    setInputValue(e.target.value)
    localStorage.setItem('homepage_input_value', e.target.value)
  }, [])

  const router = useRouter()
  const [requesting, setRequesting] = useState(false)
  const { checkOutOfCredit } = useUserCoin()

  // Dropzone functionality
  const [isDragging, setIsDragging] = useState(false)
  const { uploadAssets } = useUploadAssets({
    maxUploadAssets: 25,
  })

  const handleWorkspaceChange = (workspaceId: string) => {
    setSelectedWorkspaceId(workspaceId)
    localStorage.setItem('selected_workspace_id', workspaceId)
  }

  const handleTabChange = (tab: TabType) => {
    // Only change tab if not dragging (scrolling)
    if (!isDraggingTabs) {
      setSelectedTab(tab)
    }
  }

  /**
   * Check scroll position to determine if chevrons should be visible
   */
  const checkScrollPosition = useCallback(() => {
    const element = tabsScrollRef.current
    if (!element) {
      return
    }

    const { scrollLeft, scrollWidth, clientWidth } = element
    const scrollThreshold = 5 // Small threshold to account for rounding

    // Can scroll left if not at the beginning
    setCanScrollLeft(scrollLeft > scrollThreshold)

    // Can scroll right if not at the end
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - scrollThreshold)
  }, [])

  /**
   * Handle touch start for tab scrolling
   */
  const handleTabsTouchStart = (e: React.TouchEvent) => {
    const element = tabsScrollRef.current
    if (!element) {
      return
    }
    touchStartX.current = e.touches[0].clientX
    scrollStartLeft.current = element.scrollLeft
    setIsDraggingTabs(false)
  }

  /**
   * Handle touch move for tab scrolling
   */
  const handleTabsTouchMove = (e: React.TouchEvent) => {
    const element = tabsScrollRef.current
    if (!element) {
      return
    }

    const touchX = e.touches[0].clientX
    const diff = touchStartX.current - touchX

    // If moved more than 5px, consider it a scroll/drag
    if (Math.abs(diff) > 5) {
      setIsDraggingTabs(true)
      element.scrollLeft = scrollStartLeft.current + diff
      checkScrollPosition()
    }
  }

  /**
   * Handle touch end for tab scrolling
   */
  const handleTabsTouchEnd = () => {
    // Update chevron visibility
    checkScrollPosition()
    // Reset dragging state after a short delay to prevent click
    setTimeout(() => {
      setIsDraggingTabs(false)
    }, 100)
  }

  const gotoGen = async (
    {
      qaFlag,
      cloneData,
      redesignData,
      localBusinessData,
    }: {
      qaFlag: boolean
      cloneData?: {
        mode: 'clone'
        styleRefUrl: string | null
        userRequirements: string
        assembledPrompt: string
      }
      redesignData?: {
        mode: 'redesign'
        oldSiteUrl: string
        styleRefUrl: string | null
        improvements: string
        assembledPrompt: string
      }
      localBusinessData?: ILocalBusinessData
    } = { qaFlag: false },
  ) => {
    if (!user) {
      pushModal('LoginModal', { redirectUrl: '/' })
      return
    }
    if (requesting) {
      return
    }

    // For clone/redesign/local_business modes, use assembledPrompt; otherwise use inputValue
    const promptToUse =
      cloneData?.assembledPrompt ||
      redesignData?.assembledPrompt ||
      localBusinessData?.assembledPrompt ||
      inputValue.trim()

    if (!promptToUse) {
      return
    }

    if (user && checkOutOfCredit()) {
      return
    }

    setRequesting(true)

    if (qaFlag) {
      router.push(`/qa?message=${inputValue}`)
      localStorage.removeItem('homepage_input_value')
      localStorage.removeItem('homepage_payload')
    } else {
      // Collect all assets
      const allAssets = [
        ...payload.logoAssets,
        ...payload.referenceDesignsAssets,
        ...payload.contentAssets,
      ]

      const projectType = TAB_TO_PROJECT_TYPE[selectedTab]
      const buildOption = getTabBuildOption(selectedTab)

      // Build additional fields based on mode
      const additionalFields: Record<string, any> = {
        entry_point: 'startup',
        projectType,
        buildOption,
        initial_coding_stage: 'intake', // Use latest intake flow
        ...(selectedWorkspaceId && { workspace_id: selectedWorkspaceId }),
      }

      // Add clone/redesign specific data for backend quickscraper processing
      if (cloneData) {
        additionalFields.mode = 'clone'
        additionalFields.entry_point = 'clone'
        additionalFields.styleRefUrl = cloneData.styleRefUrl // URL to scrape for STYLE
      } else if (redesignData) {
        additionalFields.mode = 'redesign'
        additionalFields.entry_point = 'redesign'
        additionalFields.oldSiteUrl = redesignData.oldSiteUrl // URL to scrape for CONTENT
        additionalFields.styleRefUrl = redesignData.styleRefUrl // URL to scrape for STYLE (optional)
      } else if (localBusinessData) {
        additionalFields.projectType = 'local_business'
        additionalFields.entry_point = 'local_business'
        // Convert from internal ILocalBusinessData (place_id) to API ILocalBusinessInfo (placeId)
        const localBusinessInfo: ILocalBusinessInfo = {
          name: localBusinessData.businessName,
          address: localBusinessData.businessAddress,
          placeId: localBusinessData.place_id,
          style: localBusinessData.style,
        }
        additionalFields.localBusinessInfo = localBusinessInfo
      } else {
        // use the default entry_point: 'startup'
      }

      // INSTANT NAVIGATION: Store data and go to loading page immediately
      const pendingData = {
        prompt: promptToUse,
        assets: allAssets,
        additionalFields,
        mode: cloneData ? 'clone' : redesignData ? 'redesign' : undefined,
      }
      sessionStorage.setItem('pendingProjectData', JSON.stringify(pendingData))

      // Clear local storage
      setPayload(initialPayload)
      localStorage.removeItem('homepage_input_value')
      localStorage.removeItem('homepage_payload')

      // Navigate immediately - project creation happens on the pending page
      router.push('/project/pending')
    }

    sendToBothAnalytics(
      MIXPANEL_CLIENT_EVENT_NAMES.GENERATE_PROJECT,
      {
        prompt: inputValue,
        assets: [],
      },
      user?._id.toString(),
    )
  }

  const toggleStyleGrid = () => {
    setShowStyleGrid(!showStyleGrid)
  }

  const handleStyleUploadReference = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []) as File[]
      if (files.length === 0) {
        return
      }

      // Create temporary assets immediately to show files with shimmer
      const tempAssets: Asset[] = files.map((file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`
        const isImage = file.type.startsWith('image/')
        const thumbnail = isImage ? URL.createObjectURL(file) : undefined

        let assetType: Asset['type'] = 'image'
        if (file.type.startsWith('video/')) assetType = 'video'
        else if (file.type.startsWith('audio/')) assetType = 'audio'
        else if (file.type.startsWith('application/pdf')) assetType = 'pdf'
        else if (file.type.startsWith('text/csv')) assetType = 'csv'

        return {
          source: 'chat' as const,
          type: assetType,
          link: `temp://${tempId}`,
          thumbnail,
          name: 'Project needs to replicate the design (layout or content) shown in this image',
          payload: { tempId, isUploading: true },
        }
      })

      // Add temporary assets immediately (user sees them right away with shimmer)
      isInternalUpdateRef.current = true
      setPayload((prev) => ({
        ...prev,
        contentAssets: [...prev.contentAssets, ...tempAssets],
      }))

      try {
        // Upload files
        const result = await uploadAssets(files, {
          assetSource: 'chat',
          assetDir: 'chat-images',
        })

        if (!result.success) {
          toast.error(result.error, { duration: 2000 })
          // Remove temporary assets on error
          isInternalUpdateRef.current = true
          setPayload((prev) => ({
            ...prev,
            contentAssets: prev.contentAssets.filter(
              (asset) => !asset.link?.startsWith('temp://'),
            ),
          }))
          return
        }

        // Add uploaded assets with chat source and specific name
        const referenceAssets = result.assets.map((asset) => ({
          ...asset,
          source: 'chat' as const,
          name: 'Project needs to replicate the design (layout or content) shown in this image',
        }))

        // Replace temporary assets with real ones
        isInternalUpdateRef.current = true
        setPayload((prev) => {
          // Remove temp assets
          const withoutTempAssets = prev.contentAssets.filter(
            (asset) => !asset.link?.startsWith('temp://'),
          )

          // Add real assets
          return {
            ...prev,
            contentAssets: [...withoutTempAssets, ...referenceAssets],
          }
        })

        // Clean up object URLs
        tempAssets.forEach((asset) => {
          if (asset.thumbnail && asset.thumbnail.startsWith('blob:')) {
            URL.revokeObjectURL(asset.thumbnail)
          }
        })
      } catch (error) {
        // Remove temporary assets on error
        isInternalUpdateRef.current = true
        setPayload((prev) => ({
          ...prev,
          contentAssets: prev.contentAssets.filter(
            (asset) => !asset.link?.startsWith('temp://'),
          ),
        }))

        // Clean up object URLs on error
        tempAssets.forEach((asset) => {
          if (asset.thumbnail && asset.thumbnail.startsWith('blob:')) {
            URL.revokeObjectURL(asset.thumbnail)
          }
        })

        console.error('Error uploading reference design:', error)
        setErrorMessage(
          `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        )
      }

      // Reset the input
      e.target.value = ''
    },
    [uploadAssets],
  )

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation() // Prevent event from bubbling to parent
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation() // Prevent event from bubbling to parent
    // Only set dragging to false if we're leaving the main container
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false)
    }
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation() // Prevent event from bubbling to parent
      setIsDragging(false)

      const files = Array.from(e.dataTransfer.files)

      if (files.length === 0) return

      // Check upload limits with detailed error messages for content assets
      const currentCount = payload.contentAssets.length
      const selectedCount = files.length
      const totalAfterUpload = currentCount + selectedCount

      if (totalAfterUpload > MAX_CONTENT_ASSETS) {
        const remaining = Math.max(0, MAX_CONTENT_ASSETS - currentCount)
        setErrorMessage(
          modalt('upload-assets.maximum_content_error', {
            MAX_CONTENT_ASSETS,
            remaining,
          }),
        )
        return
      }

      // Create temporary assets immediately to show files with shimmer
      const tempAssets: Asset[] = files.map((file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`
        const isImage = file.type.startsWith('image/')
        const thumbnail = isImage ? URL.createObjectURL(file) : undefined

        let assetType: Asset['type'] = 'image'
        if (file.type.startsWith('video/')) assetType = 'video'
        else if (file.type.startsWith('audio/')) assetType = 'audio'
        else if (file.type.startsWith('application/pdf')) assetType = 'pdf'
        else if (file.type.startsWith('text/csv')) assetType = 'csv'

        return {
          source: 'chat' as const,
          type: assetType,
          link: `temp://${tempId}`,
          thumbnail,
          name: file.name,
          payload: { tempId, isUploading: true },
        }
      })

      // Add temporary assets immediately (user sees them right away with shimmer)
      isInternalUpdateRef.current = true
      setPayload((prev) => ({
        ...prev,
        contentAssets: [...prev.contentAssets, ...tempAssets],
      }))

      try {
        // Upload files
        const result = await uploadAssets(files, {
          assetSource: 'chat',
          assetDir: 'user-assets',
        })

        if (!result.success) {
          toast.error(result.error, { duration: 2000 })
          // Remove temporary assets on error
          isInternalUpdateRef.current = true
          setPayload((prev) => ({
            ...prev,
            contentAssets: prev.contentAssets.filter(
              (asset) => !asset.link?.startsWith('temp://'),
            ),
          }))
          return
        }

        // Replace temporary assets with real ones
        isInternalUpdateRef.current = true
        setPayload((prev) => {
          // Remove temp assets
          const withoutTempAssets = prev.contentAssets.filter(
            (asset) => !asset.link?.startsWith('temp://'),
          )

          // Add real assets
          return {
            ...prev,
            contentAssets: [...withoutTempAssets, ...result.assets],
          }
        })

        // Clean up object URLs
        tempAssets.forEach((asset) => {
          if (asset.thumbnail && asset.thumbnail.startsWith('blob:')) {
            URL.revokeObjectURL(asset.thumbnail)
          }
        })
      } catch (error) {
        // Remove temporary assets on error
        isInternalUpdateRef.current = true
        setPayload((prev) => ({
          ...prev,
          contentAssets: prev.contentAssets.filter(
            (asset) => !asset.link?.startsWith('temp://'),
          ),
        }))

        // Clean up object URLs on error
        tempAssets.forEach((asset) => {
          if (asset.thumbnail && asset.thumbnail.startsWith('blob:')) {
            URL.revokeObjectURL(asset.thumbnail)
          }
        })
        // console.error('Error uploading files:', error)
        setErrorMessage(
          `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        )
      }
    },
    [uploadAssets, payload.contentAssets.length, modalt],
  )

  // Check scroll position on mount and when element resizes
  // OPTIMIZED: Removed redundant window resize listener - ResizeObserver is sufficient
  useEffect(() => {
    checkScrollPosition()

    const element = tabsScrollRef.current
    if (!element) {
      return
    }

    // Add scroll listener
    element.addEventListener('scroll', checkScrollPosition, { passive: true })

    // Use ResizeObserver for efficient resize detection (only fires when element actually changes size)
    let rafId: number | null = null
    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to batch updates and avoid excessive calls
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(() => {
        checkScrollPosition()
      })
    })

    // Observe the container element - this is more efficient than window resize
    resizeObserver.observe(element)

    return () => {
      element.removeEventListener('scroll', checkScrollPosition)
      resizeObserver.disconnect()
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [checkScrollPosition])

  const handleRemoveAsset = (assetToRemove: Asset) => {
    // Check if it's a super prompt asset
    if (
      assetToRemove.type === 'super_prompt' &&
      assetToRemove.payload?.prompt_text
    ) {
      // Show confirmation dialog for super prompt assets
      setRemoveSuperPromptDialog({
        open: true,
        promptName:
          assetToRemove.payload?.super_prompt_name ||
          assetToRemove.name ||
          'Super Prompt',
        promptText: assetToRemove.payload?.prompt_text || '',
        onConfirm: () => {
          // Remove the prompt text from input value
          const promptTextToRemove = assetToRemove.payload?.prompt_text || ''
          if (promptTextToRemove) {
            let newInputValue = inputValue

            // Try to find and remove the exact prompt text
            if (newInputValue.includes(promptTextToRemove)) {
              // Remove the prompt text
              newInputValue = newInputValue.replace(promptTextToRemove, '')

              // Clean up extra newlines (remove up to 2 consecutive newlines left behind)
              newInputValue = newInputValue.replace(/\n{3,}/g, '\n\n')

              // Trim leading/trailing whitespace if the input is now empty or just whitespace
              if (newInputValue.trim() === '') {
                newInputValue = ''
              } else {
                // Remove leading newlines
                newInputValue = newInputValue.replace(/^\n+/, '')
                // Remove trailing newlines
                newInputValue = newInputValue.replace(/\n+$/, '')
              }

              // Update input value state and localStorage
              setInputValue(newInputValue)
              localStorage.setItem('homepage_input_value', newInputValue)
            }
          }

          // Remove the asset from payload
          isInternalUpdateRef.current = true
          setPayload((prev: typeof initialPayload) => ({
            ...prev,
            logoAssets: prev.logoAssets.filter(
              (a: Asset) => a.link !== assetToRemove.link,
            ),
            referenceDesignsAssets: prev.referenceDesignsAssets.filter(
              (a: Asset) => a.link !== assetToRemove.link,
            ),
            contentAssets: prev.contentAssets.filter(
              (a: Asset) => a.link !== assetToRemove.link,
            ),
          }))

          // Close the dialog
          setRemoveSuperPromptDialog((prev) => ({ ...prev, open: false }))
        },
      })
    } else {
      // Regular asset removal (non-super prompt)
      isInternalUpdateRef.current = true
      setPayload((prev: typeof initialPayload) => ({
        ...prev,
        logoAssets: prev.logoAssets.filter(
          (a: Asset) => a.link !== assetToRemove.link,
        ),
        referenceDesignsAssets: prev.referenceDesignsAssets.filter(
          (a: Asset) => a.link !== assetToRemove.link,
        ),
        contentAssets: prev.contentAssets.filter(
          (a: Asset) => a.link !== assetToRemove.link,
        ),
      }))
    }
  }

  const allAssets = [
    ...payload.logoAssets,
    ...payload.referenceDesignsAssets,
    ...payload.contentAssets,
  ]

  return (
    <>
      {/* CSS for animations */}
      <style jsx>{`
        @keyframes highlightBlink {
          0%, 70%, 100% {
            box-shadow: none;
          }
          15%, 55% {
            box-shadow: 0 0 0 3px rgba(255, 255, 255, 1), 0 0 20px rgba(255, 255, 255, 0.5);
          }
        }
        @keyframes fastBounce {
          0%, 100% {
            transform: translateY(0);
            animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
          }
          50% {
            transform: translateY(-15px);
            animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
          }


        }
      `}</style>

      {/* Error Banner */}
      {errorMessage && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] animate-in slide-in-from-top duration-300">
          <div className="bg-gradient-to-br from-[#2F1E30]/95 via-[#3D2A3E] to-[#2F1E30]/95 backdrop-blur-lg rounded-xl px-4 py-3 shadow-lg border border-white/10 max-w-md mx-4 relative">
            <button
              type="button"
              onClick={() => setErrorMessage(null)}
              className="absolute top-2 right-2 text-white/80 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3 pr-6">
              <AlertTriangle className="w-5 h-5 text-white flex-shrink-0" />
              <div>
                <p className="text-white font-light text-sm whitespace-nowrap">
                  {errorMessage}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Prompt Input Section */}
      <div
        ref={containerRef}
        className="mx-auto relative w-full mb-5 rounded-[20px]"
        style={{
          width: 'min(100%, 741px)',
          animation: isHighlighted
            ? 'highlightBlink 1s ease-in-out'
            : undefined,
        }}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => {
          // Delay collapse check to allow button clicks to register
          setTimeout(() => {
            // Don't collapse if any dropdown is open or focus is within the container
            if (
              showUploadDropdown ||
              showStyleGrid ||
              isWorkspaceDropdownOpen ||
              containerRef.current?.contains(document.activeElement)
            ) {
              return
            }
            setIsExpanded(false)
          }, 150)
        }}
        onFocus={() => setIsExpanded(true)}
      >
        {/* Glow Effect */}
        <div
          className={cn(
            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[102%] h-[105%] blur-[30px] md:blur-[50px] rounded-full pointer-events-none transition-opacity duration-150 ease-out',
            isExpanded ? 'bg-gold/15' : 'bg-gold/5',
          )}
        />

        <div
          className={cn(
            'relative bg-[#1a1a1a] border border-white/20 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-[border-color,box-shadow] duration-150 ease-out overflow-hidden',
            isExpanded &&
              'border-gold/40 shadow-[0_12px_40px_rgba(255,215,0,0.15)]',
            isDragging && 'bg-[#242424]',
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* No top bar - clean input first approach */}

          {/* Clone Mode Form */}
          {selectedMode === 'clone' && (
            <div className="p-5 bg-[#1f1f1f] border-b border-white/5 animate-fade-in">
              <textarea
                value={inputValue}
                onChange={onChangeInput}
                placeholder={herot('clone.textarea_placeholder')}
                rows={2}
                className="w-full bg-transparent text-white text-xs placeholder-gray-500 focus:outline-none resize-none mb-3"
              />
              {/* Copy design from - separate row */}
              <div className="flex items-center gap-2 sm:gap-3 mb-3 flex-wrap sm:flex-nowrap">
                <GradientTooltip
                  content={herot('clone.copy_design_from_tooltip')}
                >
                  <div className="flex items-center gap-1.5 text-gray-400 hover:text-gold cursor-help transition-colors shrink-0">
                    <Copy className="w-3.5 h-3.5" />
                    <span className="text-xs whitespace-nowrap">
                      {herot('clone.copy_design_from')}
                    </span>
                  </div>
                </GradientTooltip>
                <div className="flex-1 flex items-center min-w-0 border-b border-white/10 focus-within:border-gold/30 transition-colors w-full sm:w-auto">
                  <span className="text-gray-600 text-xs select-none shrink-0">
                    https://
                  </span>
                  <input
                    type="text"
                    value={cloneUrl}
                    onChange={(e) => {
                      setCloneUrl(stripUrlProtocols(e.target.value))
                    }}
                    onPaste={(e) => {
                      e.preventDefault()
                      const text = e.clipboardData.getData('text')
                      setCloneUrl(stripUrlProtocols(text))
                    }}
                    placeholder={herot('clone.url_placeholder')}
                    className="flex-1 min-w-0 bg-transparent text-white text-xs placeholder-gray-600 focus:outline-none py-1 break-all"
                  />
                </div>
                <GradientTooltip
                  content={herot('clone.upload_screenshot_tooltip')}
                >
                  <button
                    type="button"
                    onClick={() => styleUploadInputRef.current?.click()}
                    className="text-gray-500 hover:text-white transition-colors shrink-0"
                  >
                    <ImagePlus className="w-4 h-4" />
                  </button>
                </GradientTooltip>
              </div>
              {/* Bottom controls: Upload + Workspace + Generate */}
              <div className="flex items-center justify-between gap-2 sm:gap-3 pt-2 border-t border-white/5">
                <div className="flex items-center gap-2 sm:gap-3">
                  {/* Upload assets */}
                  <Popover
                    open={showUploadDropdown && selectedMode === 'clone'}
                    onOpenChange={(open) => setShowUploadDropdown(open)}
                  >
                    <GradientTooltip
                      content={herot('clone.upload_assets_tooltip')}
                    >
                      <PopoverTrigger className="text-gray-500 hover:text-white transition-colors shrink-0">
                        <Paperclip className="w-4 h-4" />
                      </PopoverTrigger>
                    </GradientTooltip>
                    <PopoverContent
                      className="w-auto p-0"
                      align="start"
                      side={isMobile ? 'top' : 'bottom'}
                    >
                      <UploadAssetsModal
                        onSave={(newPayload) => {
                          isInternalUpdateRef.current = true
                          setPayload((prev) => ({
                            ...prev,
                            logoAssets: newPayload.logoAssets,
                            contentAssets: newPayload.contentAssets,
                          }))
                          setShowUploadDropdown(false)
                        }}
                        onClose={() => setShowUploadDropdown(false)}
                        payload={{
                          logoAssets: payload.logoAssets,
                          contentAssets: payload.contentAssets,
                        }}
                        isDropdownMode={true}
                        existingContentCount={0}
                      />
                    </PopoverContent>
                  </Popover>
                  {/* Workspace selector */}
                  <WorkspaceDropdown
                    selectedWorkspaceId={selectedWorkspaceId}
                    onWorkspaceChange={handleWorkspaceChange}
                    onOpenChange={setIsWorkspaceDropdownOpen}
                    showName={!isMobile}
                  />
                </div>
                {/* Generate button */}
                <GenerateButton
                  onClick={async () => {
                    const styleRefUrl = cloneUrl.trim()
                      ? `https://${cloneUrl.trim()}`
                      : null
                    const userRequirements =
                      inputValue.trim() || 'Build me a website'

                    // Check if there are reference images uploaded via the ImagePlus button
                    const referenceImages = payload.contentAssets.filter(
                      (asset) =>
                        asset.name?.includes('replicate the design') ||
                        asset.name?.includes(
                          'replicate the design (layout or content)',
                        ),
                    )

                    // Construct assembled prompt for AI (will be first message in project)
                    let assembledPrompt = userRequirements

                    if (styleRefUrl) {
                      assembledPrompt += `\n\nClone design style from: ${styleRefUrl}`
                    }

                    // Add reference images information if any were uploaded
                    if (referenceImages.length > 0) {
                      assembledPrompt += `\n\nIMPORTANT: The uploaded reference images are design references. Please replicate the design style, layout, and aesthetics shown in these reference images.`
                    }

                    // Store clone data for backend
                    const cloneData = {
                      mode: 'clone' as const,
                      styleRefUrl,
                      userRequirements,
                      assembledPrompt, // Pass assembled prompt to gotoGen
                    }

                    // Submit directly - go to coding page
                    localStorage.removeItem('homepage_input_value')
                    gotoGen({ qaFlag: false, cloneData })
                  }}
                  disabled={
                    requesting || (!inputValue.trim() && !cloneUrl.trim())
                  }
                  requesting={requesting}
                  text={getGenerateButtonText()}
                />
              </div>
            </div>
          )}

          {/* Redesign Mode Form */}
          {selectedMode === 'redesign' && (
            <div className="p-5 bg-[#1f1f1f] border-b border-white/5 animate-fade-in">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-gray-500 text-xs shrink-0">
                  {herot('redesign.label')}
                </span>
                <div className="flex-1 flex items-center border-b border-white/10 focus-within:border-gold/30 transition-colors">
                  <span className="text-gray-600 text-xs select-none">
                    https://
                  </span>
                  <input
                    type="text"
                    value={redesignOldUrl}
                    onChange={(e) => {
                      setRedesignOldUrl(stripUrlProtocols(e.target.value))
                    }}
                    onPaste={(e) => {
                      e.preventDefault()
                      const text = e.clipboardData.getData('text')
                      setRedesignOldUrl(stripUrlProtocols(text))
                    }}
                    placeholder={herot('redesign.url_placeholder')}
                    className="flex-1 bg-transparent text-white text-xs placeholder-gray-600 focus:outline-none py-1"
                  />
                </div>
              </div>
              <textarea
                value={inputValue}
                onChange={onChangeInput}
                placeholder={herot('redesign.textarea_placeholder')}
                rows={2}
                className="w-full bg-transparent text-white text-xs placeholder-gray-500 focus:outline-none resize-none mb-3"
              />
              {/* Copy style from - separate row */}
              <div className="flex items-center gap-2 sm:gap-3 mb-3 flex-wrap sm:flex-nowrap">
                <GradientTooltip
                  content={herot('redesign.copy_style_from_tooltip')}
                >
                  <div className="flex items-center gap-1.5 text-gray-400 hover:text-gold cursor-help transition-colors shrink-0">
                    <Copy className="w-3.5 h-3.5" />
                    <span className="text-xs whitespace-nowrap">
                      {herot('redesign.copy_style_from')}
                    </span>
                  </div>
                </GradientTooltip>
                <div className="flex-1 flex items-center min-w-0 border-b border-white/10 focus-within:border-gold/30 transition-colors w-full sm:w-auto">
                  <span className="text-gray-600 text-xs select-none shrink-0">
                    https://
                  </span>
                  <input
                    type="text"
                    value={cloneUrl}
                    onChange={(e) => {
                      setCloneUrl(stripUrlProtocols(e.target.value))
                    }}
                    onPaste={(e) => {
                      e.preventDefault()
                      const text = e.clipboardData.getData('text')
                      setCloneUrl(stripUrlProtocols(text))
                    }}
                    placeholder={herot('redesign.url_placeholder_optional')}
                    className="flex-1 min-w-0 bg-transparent text-white text-xs placeholder-gray-600 focus:outline-none py-1 break-all"
                  />
                </div>
                <GradientTooltip
                  content={herot('redesign.upload_screenshot_tooltip')}
                >
                  <button
                    type="button"
                    onClick={() => styleUploadInputRef.current?.click()}
                    className="text-gray-500 hover:text-white transition-colors shrink-0"
                  >
                    <ImagePlus className="w-4 h-4" />
                  </button>
                </GradientTooltip>
              </div>
              {/* Bottom controls: Upload + Workspace + Generate */}
              <div className="flex items-center justify-between gap-2 sm:gap-3 pt-2 border-t border-white/5">
                <div className="flex items-center gap-2 sm:gap-3">
                  {/* Upload assets */}
                  <Popover
                    open={showUploadDropdown && selectedMode === 'redesign'}
                    onOpenChange={(open) => setShowUploadDropdown(open)}
                  >
                    <GradientTooltip
                      content={herot('redesign.upload_assets_tooltip')}
                    >
                      <PopoverTrigger className="text-gray-500 hover:text-white transition-colors shrink-0">
                        <Paperclip className="w-4 h-4" />
                      </PopoverTrigger>
                    </GradientTooltip>
                    <PopoverContent
                      className="w-auto p-0"
                      align="start"
                      side={isMobile ? 'top' : 'bottom'}
                    >
                      <UploadAssetsModal
                        onSave={(newPayload) => {
                          isInternalUpdateRef.current = true
                          setPayload((prev) => ({
                            ...prev,
                            logoAssets: newPayload.logoAssets,
                            contentAssets: newPayload.contentAssets,
                          }))
                          setShowUploadDropdown(false)
                        }}
                        onClose={() => setShowUploadDropdown(false)}
                        payload={{
                          logoAssets: payload.logoAssets,
                          contentAssets: payload.contentAssets,
                        }}
                        isDropdownMode={true}
                        existingContentCount={0}
                      />
                    </PopoverContent>
                  </Popover>
                  {/* Workspace selector */}
                  <WorkspaceDropdown
                    selectedWorkspaceId={selectedWorkspaceId}
                    onWorkspaceChange={handleWorkspaceChange}
                    onOpenChange={setIsWorkspaceDropdownOpen}
                    showName={!isMobile}
                  />
                </div>
                {/* Generate button */}
                <GenerateButton
                  onClick={async () => {
                    if (redesignOldUrl.trim()) {
                      const oldSiteUrl = `https://${redesignOldUrl.trim()}`
                      const styleRefUrl = cloneUrl.trim()
                        ? `https://${cloneUrl.trim()}`
                        : null
                      const improvements =
                        inputValue.trim() || 'Make it modern and professional'

                      // Check if there are reference images uploaded via the ImagePlus button
                      const referenceImages = payload.contentAssets.filter(
                        (asset) =>
                          asset.name?.includes('replicate the design') ||
                          asset.name?.includes(
                            'replicate the design (layout or content)',
                          ),
                      )

                      // Construct assembled prompt for AI (will be first message in project)
                      let assembledPrompt = `Redesign my website: ${oldSiteUrl}\n\nImprovements: ${improvements}`

                      if (styleRefUrl) {
                        assembledPrompt += `\n\nCopy design style from: ${styleRefUrl}`
                      }

                      // Add reference images information if any were uploaded
                      if (referenceImages.length > 0) {
                        assembledPrompt += `\n\nIMPORTANT: The uploaded reference images are design references. Please replicate the design style, layout, and aesthetics shown in these reference images.`
                      }

                      // Store redesign data for backend
                      const redesignData = {
                        mode: 'redesign' as const,
                        oldSiteUrl,
                        styleRefUrl,
                        improvements,
                        assembledPrompt, // Pass assembled prompt to gotoGen
                      }

                      // Submit directly - go to coding page
                      localStorage.removeItem('homepage_input_value')
                      gotoGen({ qaFlag: false, redesignData })
                    }
                  }}
                  disabled={requesting || !redesignOldUrl.trim()}
                  requesting={requesting}
                  text={getGenerateButtonText()}
                />
              </div>
            </div>
          )}

          {/* Local Business Mode Form - Same style as Redesign */}
          {selectedMode === 'local_business' && (
            <div className="p-5 bg-[#1f1f1f] border-b border-white/5 animate-fade-in">
              {/* Business Name/Address with GooglePlacesAutocomplete */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-gray-500 text-xs shrink-0 break-all max-w-20 sm:max-w-40">
                  {t('local_business.label')}
                </span>
                <div className="flex-1">
                  <GooglePlacesAutocomplete
                    onPlaceSelect={(placeId, placeName, address) => {
                      if (!placeId) {
                        return
                      }
                      setLocalBusinessPlaceId(placeId)
                      setLocalBusinessName(placeName)
                      setLocalBusinessAddress(address)
                    }}
                    placeholder={t('local_business.placeholder')}
                    value={localBusinessAddress}
                    onClear={() => {
                      setLocalBusinessPlaceId(null)
                      setLocalBusinessName('')
                      setLocalBusinessAddress('')
                    }}
                  />
                </div>
              </div>

              {/* Bottom controls - same as other tabs */}
              <div className="flex items-center justify-between gap-2 sm:gap-3 pt-2 border-t border-white/5">
                <div className="flex items-center gap-2 sm:gap-3">
                  {/* Style icon - click to expand */}
                  <GradientTooltip content="Choose design style for your project (optional)">
                    <button
                      type="button"
                      onClick={() =>
                        setShowLocalBusinessStyleGrid(
                          !showLocalBusinessStyleGrid,
                        )
                      }
                      className={cn(
                        'transition-colors shrink-0',
                        showLocalBusinessStyleGrid
                          ? 'text-blue-400'
                          : 'text-gray-500 hover:text-white',
                      )}
                    >
                      <Palette className="w-4 h-4" />
                    </button>
                  </GradientTooltip>

                  {/* Upload assets */}
                  <Popover
                    open={
                      showUploadDropdown && selectedMode === 'local_business'
                    }
                    onOpenChange={(open) => setShowUploadDropdown(open)}
                  >
                    <GradientTooltip content="Upload your logo or business images">
                      <PopoverTrigger className="text-gray-500 hover:text-white transition-colors shrink-0">
                        <Paperclip className="w-4 h-4" />
                      </PopoverTrigger>
                    </GradientTooltip>
                    <PopoverContent
                      className="w-auto p-0"
                      align="start"
                      side={isMobile ? 'top' : 'bottom'}
                    >
                      <UploadAssetsModal
                        onSave={(newPayload) => {
                          isInternalUpdateRef.current = true
                          setPayload((prev) => ({
                            ...prev,
                            logoAssets: newPayload.logoAssets,
                            contentAssets: newPayload.contentAssets,
                          }))
                          setShowUploadDropdown(false)
                        }}
                        onClose={() => setShowUploadDropdown(false)}
                        payload={{
                          logoAssets: payload.logoAssets,
                          contentAssets: payload.contentAssets,
                        }}
                        isDropdownMode={true}
                        existingContentCount={0}
                      />
                    </PopoverContent>
                  </Popover>

                  {/* Workspace - only show if multiple */}
                  {workspaces && workspaces.length > 1 && (
                    <WorkspaceDropdown
                      selectedWorkspaceId={selectedWorkspaceId}
                      onWorkspaceChange={handleWorkspaceChange}
                      onOpenChange={setIsWorkspaceDropdownOpen}
                      showName={!isMobile}
                    />
                  )}
                </div>

                {/* Generate button */}
                <GenerateButton
                  onClick={async () => {
                    if (!localBusinessPlaceId) {
                      toast.error(
                        'Please select a business from the suggestions',
                      )
                      return
                    }

                    // Always include business name, address, AND place_id in the prompt for clarity
                    // This ensures the scraper knows exactly which business at that address
                    const businessContext = `Business: "${localBusinessName}" at "${localBusinessAddress}" (place_id: ${localBusinessPlaceId})`
                    const assembledPrompt = inputValue.trim()
                      ? `${inputValue}\n\n${businessContext}`
                      : `Create a website for ${localBusinessName} at ${localBusinessAddress} (place_id: ${localBusinessPlaceId})`

                    const localBusinessData: ILocalBusinessData = {
                      mode: 'local_business',
                      businessName: localBusinessName,
                      businessAddress: localBusinessAddress,
                      place_id: localBusinessPlaceId,
                      style: '',
                      assembledPrompt,
                    }

                    localStorage.removeItem('homepage_input_value')
                    gotoGen({ qaFlag: false, localBusinessData })
                  }}
                  disabled={requesting || !localBusinessPlaceId}
                  requesting={requesting}
                  text={getGenerateButtonText()}
                />
              </div>

              {/* Expandable Style Grid - same as main input */}
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300',
                  showLocalBusinessStyleGrid
                    ? 'max-h-40 opacity-100 mt-3'
                    : 'max-h-0 opacity-0 mt-0',
                )}
              >
                <div className="flex flex-wrap gap-2 items-center">
                  {STYLES.map((style) => (
                    <button
                      key={style.id}
                      type="button"
                      onClick={() => {
                        const basePrompt = `Create a website for ${localBusinessName} at ${localBusinessAddress}`
                        const newValue = `${basePrompt}\n${style.styleText}`
                        setInputValue(newValue)
                        setShowLocalBusinessStyleGrid(false)
                      }}
                      className="px-3 py-1.5 rounded-full text-xs border border-white/10 text-gray-400 hover:text-white hover:border-white/30 hover:bg-white/5 transition-all"
                    >
                      {style.title}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => styleUploadInputRef.current?.click()}
                    className="px-3 py-1.5 rounded-full text-xs border border-dashed border-white/20 text-gray-500 hover:text-white hover:border-white/40 transition-all flex items-center gap-1.5"
                  >
                    <Upload className="w-3 h-3" />
                    Upload
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tabs Section - Hidden for cleaner UI, type selection moved to dropdown */}
          <div
            id="tabs-section"
            className={cn(
              'overflow-hidden transition-[max-height,opacity] duration-150 ease-out',
              'max-h-0 opacity-0 hidden',
            )}
          >
            <div className="px-2 sm:px-4 mx-auto relative max-w-[80vw] sm:max-w-[90vw] md:max-w-[85vw]">
              {/* Left chevron indicator - visible only on small screens when can scroll left */}
              {canScrollLeft && (
                <div className="sm:hidden absolute -left-1 top-1/2 -translate-y-1/2 pointer-events-none z-20 bg-gradient-to-r from-black/25 via-black/50 to-transparent pl-1 pr-2 py-2 transition-opacity duration-300 rounded-md">
                  <ChevronLeft className="size-4 text-white/80" />
                </div>
              )}

              {/* Scrollable tabs container */}
              <div
                ref={tabsScrollRef}
                className="overflow-x-auto no-scrollbar touch-pan-x"
                style={{ WebkitOverflowScrolling: 'touch' }}
                onTouchStart={handleTabsTouchStart}
                onTouchMove={handleTabsTouchMove}
                onTouchEnd={handleTabsTouchEnd}
              >
                <div className="inline-flex items-center gap-1 p-2 min-w-max">
                  {TABS.map((tab) => {
                    const IconComponent = TAB_ICONS[tab]
                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => handleTabChange(tab)}
                        className={cn(
                          'flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all whitespace-nowrap touch-manipulation group',
                          selectedTab === tab
                            ? 'bg-white/10 text-white shadow-sm border border-white/10'
                            : 'text-gray-500 hover:text-gold hover:bg-white/5 border border-transparent',
                        )}
                      >
                        {IconComponent && (
                          <IconComponent
                            className={cn(
                              'w-3.5 h-3.5 transition-colors',
                              selectedTab === tab
                                ? 'text-gold'
                                : 'text-gray-500 group-hover:text-gold',
                            )}
                          />
                        )}
                        {herot(`tabs.${tab}`)}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Right chevron indicator - visible only on small screens when can scroll right */}
              {canScrollRight && (
                <div className="sm:hidden absolute -right-1 top-1/2 -translate-y-1/2 pointer-events-none z-20 bg-gradient-to-l from-black/25 via-black/50 to-transparent pr-1 pl-2 py-2 transition-opacity duration-300 rounded-md">
                  <ChevronRight className="size-4 text-white/80" />
                </div>
              )}
            </div>
          </div>
          {/* Asset Pills */}
          {allAssets?.length > 0 && (
            <div
              className={cn(
                'my-2 mx-6 space-x-1 flex items-center flex-nowrap overflow-x-auto no-scrollbar ',
              )}
            >
              {allAssets.map((asset: Asset) => {
                // Check if it's a temporary uploading asset
                const isTempAsset = asset.link?.startsWith('temp://')

                // For images: check type first (works for temp assets), then check link extension
                const isImage =
                  asset.type?.startsWith('image') &&
                  (isTempAsset ||
                    asset.link?.match(/\.(png|jpg|jpeg|webp|gif)$/i))
                const isTableAsset = asset.type === 'table'
                const isSuperPromptAsset = asset.type === 'super_prompt'
                const isPdfWithThumbnail =
                  asset.type === 'pdf' && asset.thumbnail

                // 从 link 中提取文件名
                const extractFileName = (url: string) => {
                  try {
                    const urlObj = new URL(url)
                    const pathname = urlObj.pathname
                    const fileName = pathname.split('/').pop() || 'file'
                    return fileName
                  } catch {
                    // 如果不是有效的 URL，尝试从字符串中提取文件名
                    const parts = url.split('/')
                    return parts[parts.length - 1] || 'file'
                  }
                }

                const fileName = asset.name || extractFileName(asset.link || '')
                const displayName =
                  fileName.length > 15
                    ? `${fileName.replace(/\.[^/.]+$/, '').slice(0, 10)}...${fileName.match(/\.[^/.]+$/)?.[0] || ''}`
                    : fileName

                // Check if asset is uploading
                const isUploading =
                  asset.link?.startsWith('temp://') ||
                  asset.payload?.isUploading === true

                // Use asset.link as key since super prompt assets now have unique IDs
                const uniqueKey =
                  asset.link ||
                  `${asset.type}-${asset.name || 'unknown'}-${Math.random()}`

                return (
                  <div
                    className={cn(
                      'relative inline-flex items-center bg-[rgba(255,255,255,0.1)] rounded-[6px] px-[5px] py-[5px] mr-1 max-w-[120px] transition-colors duration-200',
                      (isTableAsset || isSuperPromptAsset) &&
                        'cursor-not-allowed opacity-60',
                      isUploading && 'opacity-70',
                    )}
                    key={uniqueKey}
                    title={
                      isTableAsset
                        ? "Can't Edit Table"
                        : isSuperPromptAsset
                          ? 'Super Prompt Asset'
                          : isUploading
                            ? 'Uploading...'
                            : undefined
                    }
                  >
                    {/* Shimmer overlay for uploading assets - more visible */}
                    {isUploading && (
                      <div className="absolute inset-0 rounded-[6px] overflow-hidden pointer-events-none z-[1]">
                        <div
                          className="absolute inset-0"
                          style={{
                            background:
                              'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 25%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.15) 75%, transparent 100%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 1.5s linear infinite',
                          }}
                        />
                      </div>
                    )}
                    {asset.thumbnail ? (
                      // Use thumbnail image for all asset types (image, pdf, table, video, audio, etc.)
                      <a
                        href={isUploading ? undefined : asset.link}
                        target={isUploading ? undefined : '_blank'}
                        rel={isUploading ? undefined : 'noopener noreferrer'}
                        className={cn(
                          'flex items-center relative z-[2]',
                          (isTableAsset || isSuperPromptAsset || isUploading) &&
                            'pointer-events-none',
                        )}
                        onClick={(e) => {
                          if (
                            isTableAsset ||
                            isSuperPromptAsset ||
                            isUploading
                          ) {
                            e.preventDefault()
                          }
                        }}
                      >
                        <img
                          src={asset.thumbnail}
                          alt={fileName}
                          className={cn(
                            'w-5 h-5 object-cover rounded-md mr-1.5 flex-shrink-0',
                            isUploading && 'opacity-60',
                          )}
                        />
                        <span
                          className="text-xs text-white truncate max-w-[60px]"
                          title={fileName}
                        >
                          {displayName}
                        </span>
                      </a>
                    ) : isSuperPromptAsset ? (
                      // Fallback for super prompt assets without thumbnail
                      <div className="flex items-center">
                        <span className="flex items-center justify-center w-5 h-5 mr-1.5 flex-shrink-0">
                          <SuperPromptsIcon className="w-4 h-4 text-white opacity-60" />
                        </span>
                        <span
                          className="text-xs text-white truncate max-w-[60px]"
                          title={fileName}
                        >
                          {displayName}
                        </span>
                      </div>
                    ) : (
                      // Fallback for assets without thumbnail (use icon)
                      <a
                        href={isUploading ? undefined : asset.link}
                        target={isUploading ? undefined : '_blank'}
                        rel={isUploading ? undefined : 'noopener noreferrer'}
                        className={cn(
                          'flex items-center relative z-[2]',
                          isUploading && 'pointer-events-none',
                        )}
                        onClick={(e) => {
                          if (isUploading) {
                            e.preventDefault()
                          }
                        }}
                      >
                        <span className="flex items-center justify-center w-5 h-5 mr-1.5 flex-shrink-0">
                          {(() => {
                            const IconComponent = getFileIcon(
                              asset.type,
                              fileName,
                            )
                            return (
                              <IconComponent className="w-4 h-4 text-white opacity-60" />
                            )
                          })()}
                        </span>
                        <span
                          className="text-xs text-white truncate max-w-[60px]"
                          title={fileName}
                        >
                          {displayName}
                        </span>
                      </a>
                    )}

                    {isUploading ? (
                      <div className="p-0.5 rounded-full relative z-20 flex items-center justify-center">
                        <Loader2 className="w-3 h-3 text-gold animate-spin" />
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="p-0.5 rounded-full transition-colors duration-200 relative z-20 hover:bg-white/10"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemoveAsset(asset)
                        }}
                        aria-label="delete"
                      >
                        <X className="w-4 h-4 text-gray-300 hover:text-white" />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Main Input Section - Clean & Simple */}
          {!selectedMode && (
            <div className="p-5 bg-[#1f1f1f]">
              {/* Input Area with tools inside */}
              <div className="relative mb-4">
                {/* Animated Placeholder Overlay */}
                {!inputValue.trim() && (
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none text-xs font-light text-gray-500 flex flex-wrap items-start pt-1 select-none leading-relaxed">
                    <span
                      className={cn(
                        'transition-opacity duration-500',
                        placeholderFade ? 'opacity-100' : 'opacity-0',
                      )}
                    >
                      {(() => {
                        try {
                          const placeholderExamples = herot.raw(
                            'placeholder_examples',
                          ) as string[]
                          if (
                            Array.isArray(placeholderExamples) &&
                            placeholderExamples.length > 0
                          ) {
                            return placeholderExamples[
                              placeholderIndex % placeholderExamples.length
                            ]
                          }
                          const fallback = herot(
                            `placeholder_examples.${placeholderIndex}`,
                          )
                          if (
                            fallback &&
                            !fallback.includes('placeholder_examples')
                          ) {
                            return fallback
                          }
                        } catch (error) {
                          console.error(
                            'Error loading placeholder examples:',
                            error,
                          )
                        }
                        return ''
                      })()}
                    </span>
                  </div>
                )}

                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={onChangeInput}
                  onFocus={() => setIsExpanded(true)}
                  onCompositionStart={() => setIsComposing(true)}
                  onCompositionEnd={() => setIsComposing(false)}
                  className={cn(
                    'w-full bg-transparent border-none focus:ring-0 text-white text-xs font-light placeholder-transparent resize-none leading-relaxed outline-none transition-all duration-200 relative z-10',
                    isExpanded ? 'min-h-[80px]' : 'min-h-[60px]',
                  )}
                  placeholder=""
                  spellCheck={false}
                />
              </div>

              {/* Tools and Generate Button */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Popover
                    open={showUploadDropdown}
                    onOpenChange={setShowUploadDropdown}
                  >
                    <GradientTooltip content={herot('tooltip_upload')}>
                      <PopoverTrigger className="text-gray-500 hover:text-gold transition-colors">
                        <Paperclip className="w-4 h-4" />
                      </PopoverTrigger>
                    </GradientTooltip>
                    <PopoverContent
                      className="w-auto p-0"
                      align="start"
                      side={isMobile ? 'top' : 'bottom'}
                    >
                      <UploadAssetsModal
                        onSave={(newPayload) => {
                          isInternalUpdateRef.current = true
                          setPayload((prev) => ({
                            ...prev,
                            logoAssets: newPayload.logoAssets,
                            contentAssets: newPayload.contentAssets,
                          }))
                          setShowUploadDropdown(false)
                        }}
                        onClose={() => setShowUploadDropdown(false)}
                        payload={{
                          logoAssets: payload.logoAssets,
                          contentAssets: payload.contentAssets,
                        }}
                        isDropdownMode={true}
                        existingContentCount={0}
                      />
                    </PopoverContent>
                  </Popover>

                  <GradientTooltip content={herot('tooltip_style')}>
                    <button
                      type="button"
                      onClick={toggleStyleGrid}
                      className={cn(
                        'transition-colors',
                        showStyleGrid
                          ? 'text-gold'
                          : 'text-gray-500 hover:text-gold',
                      )}
                    >
                      <Palette className="w-4 h-4" />
                    </button>
                  </GradientTooltip>

                  <WorkspaceDropdown
                    selectedWorkspaceId={selectedWorkspaceId}
                    onWorkspaceChange={handleWorkspaceChange}
                    onOpenChange={setIsWorkspaceDropdownOpen}
                    showName={!isMobile}
                  />
                </div>

                <GenerateButton
                  onClick={() => gotoGen({ qaFlag: false })}
                  disabled={requesting || !inputValue.trim()}
                  requesting={requesting}
                  showFreeLabel={false}
                  text={getGenerateButtonText()}
                />
              </div>

              {/* INLINE STYLE GRID (Expandable) - Compact Design */}
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300',
                  showStyleGrid
                    ? 'max-h-40 opacity-100 mt-3'
                    : 'max-h-0 opacity-0 mt-0',
                )}
              >
                <div className="flex flex-wrap gap-2 items-center">
                  {STYLES.map((style) => (
                    <button
                      key={style.id}
                      type="button"
                      onClick={() => {
                        const styleText = style.styleText
                        const newValue = inputValue.trim()
                          ? `${inputValue}\n${styleText}`
                          : styleText
                        setInputValue(newValue)
                        localStorage.setItem('homepage_input_value', newValue)
                        setShowStyleGrid(false)
                      }}
                      className="px-3 py-1.5 rounded-full text-xs border border-white/10 text-gray-400 hover:text-white hover:border-white/30 hover:bg-white/5 transition-all"
                    >
                      {style.title}
                    </button>
                  ))}

                  {/* Upload Option */}
                  <button
                    type="button"
                    onClick={() => styleUploadInputRef.current?.click()}
                    className="px-3 py-1.5 rounded-full text-xs border border-dashed border-white/20 text-gray-500 hover:text-white hover:border-white/40 transition-all flex items-center gap-1.5"
                  >
                    <Upload className="w-3 h-3" />
                    Upload
                  </button>

                  {/* Clone from URL Option */}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedMode('clone')
                      setShowStyleGrid(false)
                    }}
                    className="px-3 py-1.5 rounded-full text-xs border border-white/10 text-gray-400 hover:text-white hover:border-white/30 hover:bg-white/5 transition-all flex items-center gap-1.5"
                  >
                    <Copy className="w-3 h-3" />
                    Clone from URL
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mode Cards - below input container */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* New Idea Card */}
          <button
            type="button"
            onClick={() => {
              setSelectedMode(null)
              setInputValue('')
              setCloneUrl('')
              setRedesignOldUrl('')
              setLocalBusinessName('')
              setLocalBusinessAddress('')
              setLocalBusinessPlaceId(null)
            }}
            className={cn(
              'p-4 rounded-lg border transition-all text-left',
              selectedMode === null
                ? 'bg-gold/10 border-gold/30'
                : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20',
            )}
          >
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="text-sm font-medium text-white">
                {herot('mode_cards.new_idea.title')}
              </span>
            </div>
          </button>

          {/* Clone Website Card */}
          <button
            type="button"
            onClick={() => setSelectedMode('clone')}
            className={cn(
              'p-4 rounded-lg border transition-all text-left',
              selectedMode === 'clone'
                ? 'bg-gold/10 border-gold/30'
                : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20',
            )}
          >
            <div className="flex items-center gap-2">
              <Copy className="w-4 h-4 text-gold" />
              <span className="text-sm font-medium text-white">
                {herot('mode_cards.clone_website.title')}
              </span>
            </div>
          </button>

          {/* Local Business Card */}
          <button
            type="button"
            onClick={() => setSelectedMode('local_business')}
            className={cn(
              'p-4 rounded-lg border transition-all text-left',
              selectedMode === 'local_business'
                ? 'bg-gold/10 border-gold/30'
                : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20',
            )}
          >
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gold" />
              <span className="text-sm font-medium text-white">
                {herot('mode_cards.local_business.title')}
              </span>
            </div>
          </button>

          {/* Redesign Site Card */}
          <button
            type="button"
            onClick={() => setSelectedMode('redesign')}
            className={cn(
              'p-4 rounded-lg border transition-all text-left',
              selectedMode === 'redesign'
                ? 'bg-gold/10 border-gold/30'
                : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20',
            )}
          >
            <div className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-gold" />
              <span className="text-sm font-medium text-white">
                {herot('mode_cards.redesign_site.title')}
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* Full-screen drag overlay */}
      {isDragging && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm z-50">
          <div className="flex flex-col items-center text-white">
            <Image
              src="/ai-chat/upload.png"
              width={150}
              height={150}
              alt="upload"
              className="mb-6"
              style={{
                animation: 'fastBounce 0.75s ease-in-out infinite',
              }}
            />
            <h2 className="text-2xl font-bold mb-3">
              {herot('upload_files_overlay_title')}
            </h2>
            <p className="text-lg text-gray-300 text-center max-w-md">
              {herot('upload_files_overlay_description')}
            </p>
          </div>
        </div>
      )}

      {/* Remove Super Prompt Confirmation Dialog */}
      <Dialog
        open={removeSuperPromptDialog.open}
        onOpenChange={(open) => {
          return setRemoveSuperPromptDialog((prev) => ({ ...prev, open }))
        }}
      >
        <DialogContent
          className="max-w-md bg-gradient-to-br from-[#222222]/95 via-[#2B2B2B] to-[#010101]/95 backdrop-blur-lg border-white/10"
          overlayClassName="bg-black/60 backdrop-blur-sm"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white">
              {modalt('remove-super-prompt-confirmation.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="mb-4">
            <p className="text-white/80 text-sm mb-3">
              {modalt('remove-super-prompt-confirmation.description')}
            </p>
            <div className="bg-black/30 rounded-lg p-3 border border-white/10">
              <p className="text-sm text-yellow-300 font-mono break-all">
                {modalt(
                  'remove-super-prompt-confirmation.remove_line_instruction',
                  {
                    lineToRemove: (() => {
                      const promptText = removeSuperPromptDialog.promptText
                      // Find the part before the first URL (http/https)
                      const urlMatch = promptText.match(/(https?:\/\/[^\s]+)/)
                      if (urlMatch && urlMatch[0]) {
                        const beforeUrl = promptText
                          .substring(0, promptText.indexOf(urlMatch[0]))
                          .trim()
                        return beforeUrl.endsWith(':')
                          ? beforeUrl
                          : `${beforeUrl}:`
                      }
                      // If no URL found, return the whole prompt text line
                      return promptText.trim()
                    })(),
                  },
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <Button
              variant={'outline_2'}
              onClick={() => {
                return setRemoveSuperPromptDialog((prev) => ({
                  ...prev,
                  open: false,
                }))
              }}
            >
              {modalt('remove-super-prompt-confirmation.cancel')}
            </Button>
            <Button
              variant={'active'}
              onClick={removeSuperPromptDialog.onConfirm}
            >
              {modalt('remove-super-prompt-confirmation.confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden File Input for Style Reference Upload */}
      <input
        ref={styleUploadInputRef}
        type="file"
        className="hidden"
        accept=".jpg,.png"
        multiple
        onChange={handleStyleUploadReference}
      />
    </>
  )
}
